import {z} from 'zod';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {depositSchema} from '@dms/settings/shared';

const documentSeriesSchema = z.object({
  docSeriesId: z.string().nullish(),
});

const templateAndDocSeriesSchema = z.object({
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const fullPaymentDocumentsSchema = z.object({
  invoice: templateAndDocSeriesSchema.nullish(),
  deposit: documentSeriesSchema.nullish(),
  invoiceCorrection: documentSeriesSchema.nullish(),
  taxDocument: documentSeriesSchema.nullish(),
  taxDocumentCorrection: documentSeriesSchema.nullish(),
  invoiceEu: documentSeriesSchema.nullish(),
  invoiceEuCorrection: documentSeriesSchema.nullish(),
  invoiceOutsideEu: documentSeriesSchema.nullish(),
  invoiceOutsideEuCorrection: documentSeriesSchema.nullish(),
});

const internalPaymentDocumentsSchema = z.object({
  invoice: templateAndDocSeriesSchema.nullish(),
  invoiceCorrection: documentSeriesSchema.nullish(),
});

const basePaymentSchema = z.object({
  isEnable: z.boolean(),
});

// Only cash, card and free of charge carry auto-initiated payments; bank transfer and cash on
// delivery have no such field in the request contract and must not gain one.
const autoInitiatePaymentSchema = z.object({
  isAutoInitiatePayment: z.boolean().nullish(),
});

const fullPaymentSchema = basePaymentSchema.merge(fullPaymentDocumentsSchema);
const cardPaymentSchema = fullPaymentSchema.merge(autoInitiatePaymentSchema);
const cashPaymentSchema = cardPaymentSchema.merge(z.object({cashRegisterId: z.string().nullish()}));
const internalPaymentSchema = basePaymentSchema
  .merge(internalPaymentDocumentsSchema)
  .merge(autoInitiatePaymentSchema);

const validateFullPaymentFields = (
  data: z.infer<typeof fullPaymentSchema>,
  ctx: z.RefinementCtx
) => {
  if (!data.isEnable) {
    return;
  }

  const requiredMessage = i18n.t('errors.field_is_required');

  if (isNil(data.invoice?.docSeriesId) || data.invoice?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoice?.templateId) || data.invoice?.templateId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'templateId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.deposit?.docSeriesId) || data.deposit?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['deposit', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceCorrection?.docSeriesId) || data.invoiceCorrection?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.taxDocument?.docSeriesId) || data.taxDocument?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taxDocument', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (
    isNil(data.taxDocumentCorrection?.docSeriesId) ||
    data.taxDocumentCorrection?.docSeriesId === ''
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taxDocumentCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceEu?.docSeriesId) || data.invoiceEu?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceEu', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (
    isNil(data.invoiceEuCorrection?.docSeriesId) ||
    data.invoiceEuCorrection?.docSeriesId === ''
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceEuCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceOutsideEu?.docSeriesId) || data.invoiceOutsideEu?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceOutsideEu', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (
    isNil(data.invoiceOutsideEuCorrection?.docSeriesId) ||
    data.invoiceOutsideEuCorrection?.docSeriesId === ''
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceOutsideEuCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }
};

const validateInternalPaymentFields = (
  data: z.infer<typeof internalPaymentSchema>,
  ctx: z.RefinementCtx
) => {
  if (!data.isEnable) {
    return;
  }

  const requiredMessage = i18n.t('errors.field_is_required');

  if (isNil(data.invoice?.docSeriesId) || data.invoice?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoice?.templateId) || data.invoice?.templateId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'templateId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceCorrection?.docSeriesId) || data.invoiceCorrection?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }
};

const validateCashPaymentFields = (
  data: z.infer<typeof cashPaymentSchema>,
  ctx: z.RefinementCtx
) => {
  validateFullPaymentFields(data, ctx);

  if (!data.isEnable || !data.isAutoInitiatePayment) {
    return;
  }

  if (isNil(data.cashRegisterId) || data.cashRegisterId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cashRegisterId'],
      message: i18n.t('errors.field_is_required'),
    });
  }
};

const fullPaymentSchemaWithValidation = fullPaymentSchema.superRefine(validateFullPaymentFields);
const cardPaymentSchemaWithValidation = cardPaymentSchema.superRefine(validateFullPaymentFields);
const cashPaymentSchemaWithValidation = cashPaymentSchema.superRefine(validateCashPaymentFields);
const internalPaymentSchemaWithValidation = internalPaymentSchema.superRefine(
  validateInternalPaymentFields
);

const MAX_MARGIN = 100;

const generalSchema = z.object({
  isActive: z.boolean(),
  directSaleVariantId: z.string().nullish(),
  directSaleTypeId: z.string().nonempty(),
  name: z.string().nonempty(),
  note: z.string().optional(),
  priceType: z.string().nonempty(),
  rateType: z.string().nonempty(),
  priceTypeRatio: z.number().positive().nullish(),
  rateTypeRatio: z.number().positive().nullish(),
  minimalMaterialMargin: z
    .number()
    .max(MAX_MARGIN, {
      message: i18n.t('entity.warehouse.validations.maxMargin', {max: MAX_MARGIN}),
    })
    .nullish(),
  minimalWorkMargin: z
    .number()
    .max(MAX_MARGIN, {
      message: i18n.t('entity.warehouse.validations.maxMargin', {max: MAX_MARGIN}),
    })
    .nullish(),
  defaultCustomerId: z.string().nullish(),
  sufix: z.string().nonempty(),
  documentTemplates: z.string().nullish(),
  authorizationProfileId: z.string().nonempty(),
  docSeriesId: z.string().nonempty(),
  isUnitPriceWithVat: z.boolean(),
  isChangeableDefaultCustomer: z.boolean(),
  restrictSaleBelowCost: z.boolean(),
  branchId: z.string(),
});

const orderOfferSchema = z.object({
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const fastTrackIssueSchema = z.object({
  isEnable: z.boolean(),
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const validateFastTrackIssueFields = (
  data: z.infer<typeof fastTrackIssueSchema>,
  ctx: z.RefinementCtx
) => {
  if (!data.isEnable) {
    return;
  }

  const requiredMessage = i18n.t('errors.field_is_required');

  if (isNil(data.docSeriesId) || data.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['docSeriesId'],
      message: requiredMessage,
    });
  }
};

const fastTrackIssueSchemaWithValidation = fastTrackIssueSchema.superRefine(
  validateFastTrackIssueFields
);

const defaultPaymentTypeSchema = z.enum([
  'CASH',
  'CARD',
  'BANK_TRANSFER',
  'INTERNAL',
  'CASH_ON_DELIVERY',
]);

const documentsSchema = z.object({
  defaultPaymentType: defaultPaymentTypeSchema.nullish(),
  orderOffer: orderOfferSchema.nullish(),
  cashPayment: cashPaymentSchemaWithValidation,
  cashOnDeliveryPayment: fullPaymentSchemaWithValidation,
  cardPayment: cardPaymentSchemaWithValidation,
  bankPayment: fullPaymentSchemaWithValidation,
  internalPayment: internalPaymentSchemaWithValidation,
  fastTrackIssue: fastTrackIssueSchemaWithValidation,
});

const warehousePriorityItemSchema = z.object({
  warehouseId: z.string(),
  warehouseName: z.string(),
  warehouseNumber: z.string(),
  warehousePriority: z.number(),
  authorizationProfile: z.object({
    id: z.string(),
    name: z.string(),
  }),
  branch: z.object({
    id: z.string(),
    name: z.string(),
  }),
});

const warehousesPrioritySchema = z.array(warehousePriorityItemSchema);

const accountingSchema = z
  .object({
    directSaleVariantId: z.string().nullish(),
    isTaxDeductible: z.string().nullish(),
    fixedVatType: z.string().nullish(),
    excludedServiceCenterIds: z.array(z.string()).nullish(),
    defaultServiceCenterId: z.string().nullish(),
    isDefaultServiceCenterLocked: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.isTaxDeductible === 'false' && isNil(data.fixedVatType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('errors.field_is_required'),
        path: ['fixedVatType'],
      });
    }
  });

const reportingSchema = z.object({
  directSaleVariantId: z.string().nullish(),
  excludedServiceOrderGroupIds: z.array(z.string()).nullish(),
  defaultServiceOrderGroupId: z.string().nullish(),
  isDefaultServiceOrderGroupLocked: z.boolean().nullish(),
  isPreDeliveryInspection: z.boolean().nullish(),
});

export const WarehouseDirectSaleVariantsDetailFormSchema = z.object({
  general: generalSchema,
  documents: documentsSchema,
  deposit: depositSchema,
  accounting: accountingSchema,
  reporting: reportingSchema,
  warehousesPriority: warehousesPrioritySchema,
});

export type WarehouseDirectSaleVariantsDetailFormSchemaType = z.infer<
  typeof WarehouseDirectSaleVariantsDetailFormSchema
>;
